"use client";

import { usePagination } from "react-instantsearch";
import { ReadonlyURLSearchParams } from "next/navigation";
import { ChevronRightIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import { RESULT_PAGE_URL_PARAM } from "@/components/Search/constants";
import { useEffect } from "react";
import useTranslations from "@/i18n/useTranslations";
import {
  dispatchSearchPageChange,
  dispatchSearchUrlChange,
  pageIndexFromSearch,
  SEARCH_PAGE_CHANGE_EVENT,
} from "@/components/Search/hooks/useSyncSearchPage";

interface SearchPaginationProps {
  searchParams: ReadonlyURLSearchParams;
}

function SearchPagination({ searchParams }: SearchPaginationProps) {
  const t = useTranslations("Search");
  const { pages, currentRefinement, refine, nbPages, isFirstPage, isLastPage } =
    usePagination({
      padding: 2,
    });

  const setPageLink = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(RESULT_PAGE_URL_PARAM, String(page + 1));
    const isInSearchPage = window.location.pathname.includes("/search");
    if (!isInSearchPage) {
      // Hard reload. Search results embedded on other pages should open /search.
      window.location.href = `search?${params.toString()}`;
      return;
    }
    window.history.pushState({}, "", `?${params.toString()}`);
    dispatchSearchPageChange();
    dispatchSearchUrlChange();
  };

  useEffect(() => {
    const refineFromUrl = () => {
      refine(pageIndexFromSearch(window.location.search));
    };
    const handlePopState = (event: PopStateEvent) => {
      if (!event.isTrusted) return;
      refineFromUrl();
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener(SEARCH_PAGE_CHANGE_EVENT, refineFromUrl);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener(SEARCH_PAGE_CHANGE_EVENT, refineFromUrl);
    };
  }, [refine]);

  if (nbPages < 2) {
    return null;
  }

  const handleClick = (page: number) => {
    setPageLink(page);
  };

  const baseClasses =
    "px-3 py-1 mr-0 text-sm hover:bg-secondary-400 hover:text-white";
  const activeClasses = "bg-primary text-white border-double border-4";

  const upTo4Pages = () => {
    return (
      <>
        {pages.map((page, index) => (
          <button
            key={page}
            onClick={() => handleClick(page)}
            className={`${baseClasses} ${currentRefinement === page ? activeClasses : ""}`}
            aria-label={
              pages.length === index + 1
                ? `Last page, page ${nbPages}`
                : `Page ${page + 1}`
            }
            aria-current={currentRefinement === page ? "page" : "false"}
          >
            {page + 1}
          </button>
        ))}
      </>
    );
  };

  const firstThreePages = () => {
    return (
      <>
        {pages.slice(0, 3).map((page) => (
          <button
            key={page}
            onClick={() => handleClick(page)}
            className={`${baseClasses} ${currentRefinement === page ? activeClasses : ""}`}
            aria-label={t("page", { number: page + 1 })}
            aria-current={currentRefinement === page ? "page" : "false"}
          >
            {page + 1}
          </button>
        ))}
        <span>...</span>
        <button
          key={nbPages - 1}
          onClick={() => handleClick(nbPages - 1)}
          className={`${baseClasses} ${currentRefinement === nbPages ? activeClasses : ""}`}
          aria-label={t("lastPage", { number: nbPages })}
          aria-current={currentRefinement === nbPages ? "page" : "false"}
        >
          {nbPages}
        </button>
      </>
    );
  };

  const lastThreePages = () => {
    return (
      <>
        <button
          key={0}
          onClick={() => handleClick(0)}
          className={`${baseClasses} ${currentRefinement === 0 ? activeClasses : ""}`}
          aria-label={t("pageOne")}
          aria-current={currentRefinement === 0 ? "page" : "false"}
        >
          1
        </button>
        <span>...</span>
        {pages.slice(-3).map((page, index) => (
          <button
            key={page}
            onClick={() => handleClick(page)}
            className={`${baseClasses} ${currentRefinement === page ? activeClasses : ""}`}
            aria-label={
              pages.slice(-3).length === index + 1
                ? `Last page, page ${nbPages}`
                : `Page ${page + 1}`
            }
            aria-current={currentRefinement === page ? "page" : "false"}
          >
            {page + 1}
          </button>
        ))}
      </>
    );
  };

  const middle = () => {
    return (
      <>
        <button
          key={0}
          onClick={() => handleClick(0)}
          className={`${baseClasses} ${currentRefinement === 0 ? activeClasses : ""}`}
          aria-label={t("pageOne")}
          aria-current={currentRefinement === 0 ? "page" : "false"}
        >
          1
        </button>
        <span className="pr-2">...</span>
        <button
          key={currentRefinement}
          onClick={() => handleClick(currentRefinement)}
          className={`${baseClasses} ${activeClasses}`}
          aria-label={t("page", { number: currentRefinement + 1 })}
          aria-current="page"
        >
          {currentRefinement + 1}
        </button>
        <span className="pl-2">...</span>
        <button
          key={nbPages - 1}
          onClick={() => handleClick(nbPages - 1)}
          className={`${baseClasses} ${currentRefinement === nbPages - 1 ? activeClasses : ""}`}
          aria-label={t("lastPage", { number: nbPages })}
          aria-current={currentRefinement === nbPages - 1 ? "page" : "false"}
        >
          {nbPages}
        </button>
      </>
    );
  };

  return (
    <div className="flex flex-row items-center">
      <button
        className="p-2"
        onClick={() => handleClick(currentRefinement - 1)}
        disabled={isFirstPage}
        aria-label={t("previousPage")}
      >
        <ChevronLeftIcon
          className={`${isFirstPage ? "stroke-putty-dark" : "w-4 h-4 text-primary-500 bg-white stroke-primary-500 stroke-1 hover:rounded-full hover:bg-primary-500 hover:stroke-white"}`}
        />
      </button>

      <div className="block md:hidden flex flex-col justify-end items-end text-sm whitespace-nowrap">
        <span>
          {" "}
          {t("pageOf", {
            current: currentRefinement + 1,
            total: nbPages,
          })}{" "}
        </span>
      </div>
      <div className="hidden md:block">
        {nbPages <= 4
          ? upTo4Pages()
          : currentRefinement < 3
            ? firstThreePages()
            : currentRefinement >= nbPages - 3
              ? lastThreePages()
              : middle()}
      </div>

      <button
        className="p-2"
        onClick={() => handleClick(currentRefinement + 1)}
        disabled={isLastPage}
        aria-label={t("nextPage")}
      >
        <ChevronRightIcon
          className={`${isLastPage ? "stroke-putty-dark" : "w-4 h-4 text-primary-500 bg-white stroke-primary-500 stroke-1 hover:rounded-full hover:bg-primary-500 hover:stroke-white"}`}
        />
      </button>
    </div>
  );
}

export default SearchPagination;
